import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import { ArrowClockwise, CheckCircle } from 'react-bootstrap-icons';
import { ClientTags } from '@/components/clientFlags/ClientTags';
import { MarkInactiveDialog } from '@/components/clientChecks/MarkInactiveDialog';
import {
  CHECKS,
  formatDay,
  nzToday,
  runChecks,
  stackUse,
  trialRows,
  type CheckDef,
  type Finding,
  type StackUse,
} from '@/components/clientChecks/clientChecks';
import { Empty, Kpi, KpiGrid, Panel, Section } from '@/components/NumaDashboard/ui/kit';
import { useFleetData } from '@/components/NumaDashboard/useFleetData';
import { PageHeader } from '@/components/ui/PageHeader';
import { useClientFlags } from '@/hooks/useClientFlags';
import { clientService } from '@/services/clientService';
import type { Client } from '@/types';
import type { ClientSnapshot } from '@/types/fleetAnalytics';
import { formatNzDateTime } from '@/utils/relativeTime';
import './ClientChecks.css';

/**
 * Client checks: the trials, and the clients whose records or use need a look.
 *
 * The page only reads when it opens: the client configs, the portal's own
 * record of each client, and the fleet index the nightly rollup builds. The
 * one write is "Mark inactive", and it waits for a person to press it and
 * confirm. The rules are in `components/clientChecks/clientChecks.ts`.
 */

const changeConfigLink = (name: string) => `/tools/update-client-config?clientName=${encodeURIComponent(name)}`;
const configsLink = (name: string) => `/configs?client=${encodeURIComponent(name)}`;

const count = (n: number | null | undefined) => (n == null ? '' : Math.round(n).toLocaleString());

export default function ClientChecks() {
  const flags = useClientFlags();
  const fleet = useFleetData();
  const [clients, setClients] = useState<Client[] | null>(null);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [marking, setMarking] = useState<Finding | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const today = useMemo(() => nzToday(), []);

  const loadClients = useCallback(async () => {
    setClientsError(null);
    try {
      setClients(await clientService.getAllClients());
    } catch (e) {
      setClients(null);
      setClientsError(e instanceof Error ? e.message : 'The client list did not load.');
    }
  }, []);

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  const refresh = () => {
    setNotice(null);
    clientService.clearCache();
    void loadClients();
    void flags.reload();
    fleet.reload();
  };

  // Until the tags are read, every client reads as active. The checks wait, so no
  // inactive client is offered for marking inactive.
  const tagsReady = flags.status === 'ready' || (flags.status === 'error' && flags.metadata.size > 0);

  const byName = useMemo(() => {
    const map = new Map<string, ClientSnapshot>();
    for (const snapshot of fleet.snapshots) map.set(snapshot.client, snapshot);
    return map;
  }, [fleet.snapshots]);
  const fleetReady = !fleet.loading && !fleet.error && fleet.source !== null;

  const findings = useMemo(() => {
    if (!clients || !tagsReady) return [];
    return runChecks({
      clients,
      row: flags.row,
      isInactive: flags.isInactive,
      fleet: fleetReady ? { snapshot: (name) => byName.get(name), status: (name) => fleet.status[name] } : null,
      today,
    });
  }, [clients, tagsReady, flags, fleetReady, byName, fleet.status, today]);

  const trials = useMemo(
    () => (clients && tagsReady ? trialRows(clients, flags.row, flags.isInactive, today) : []),
    [clients, tagsReady, flags, today]
  );
  const stackUseOf = (name: string): StackUse | null => {
    const snapshot = byName.get(name);
    return snapshot ? stackUse(snapshot, today) : null;
  };

  const configOf = (name: string) => clients?.find((c) => c.name === name)?.config;
  const problems = findings.filter((f) => f.check !== 'no-stack');
  const shownChecks = CHECKS.map((def) => ({ def, rows: findings.filter((f) => f.check === def.id) })).filter(
    (c) => c.rows.length > 0
  );
  const ended = trials.filter((t) => t.timing.state === 'ended').length;
  const undated = trials.filter((t) => t.timing.state === 'no-end').length;
  const ready = clients !== null && tagsReady;
  const waiting = <Spinner animation="border" size="sm" />;

  const clientCell = (name: string) => (
    <td>
      <span className="client-checks__name">{name}</span>
      <ClientTags clientName={name} flags={flags} config={configOf(name)} hide="deploy-block" />
    </td>
  );

  const actionsFor = (def: CheckDef, finding: Finding) =>
    def.actions.map((action) => {
      switch (action) {
        case 'change-config':
          return (
            <Link key={action} to={changeConfigLink(finding.clientName)} className="btn btn-sm btn-outline-secondary">
              Change the config
            </Link>
          );
        case 'open-configs':
          return (
            <Link key={action} to={configsLink(finding.clientName)} className="btn btn-sm btn-outline-secondary">
              Open in Configs
            </Link>
          );
        case 'mark-inactive':
          return (
            <Button
              key={action}
              size="sm"
              variant="outline-secondary"
              disabled={flags.isInactive(finding.clientName)}
              onClick={() => {
                setNotice(null);
                setMarking(finding);
              }}
            >
              {flags.isInactive(finding.clientName) ? 'Inactive' : 'Mark inactive'}
            </Button>
          );
        case 'none':
          return (
            <span key={action} className="client-checks__none">
              Nothing to do yet
            </span>
          );
      }
    });

  return (
    <div className="client-checks">
      <PageHeader
        title="Client checks"
        description="Trials, and clients whose records or use need a look. Nothing changes until you press a button."
        actions={
          <Button variant="outline-secondary" onClick={refresh}>
            <ArrowClockwise className="me-2" />
            Refresh
          </Button>
        }
      />

      {clientsError && (
        <Alert variant="danger">
          The client list did not load, so no check can run. {clientsError}{' '}
          <Button size="sm" variant="outline-secondary" onClick={() => void loadClients()}>
            Try again
          </Button>
        </Alert>
      )}
      {flags.status === 'error' && !tagsReady && (
        <Alert variant="danger">
          The client tags and contracts did not load, so no check can run. {flags.error}{' '}
          <Button size="sm" variant="outline-secondary" onClick={() => void flags.reload()}>
            Try again
          </Button>
        </Alert>
      )}
      {notice && (
        <Alert variant="success" dismissible onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Section title="Summary">
        <KpiGrid cols={3}>
          <Kpi
            label="Trials"
            scope="now"
            value={ready ? trials.length : waiting}
            tone={ended > 0 ? 'warn' : 'default'}
            sub={ready ? `${ended.toLocaleString()} ended, ${undated.toLocaleString()} with no end date` : ''}
          />
          <Kpi
            label="Problems found"
            scope="now"
            value={ready ? problems.length : waiting}
            tone={problems.length > 0 ? 'bad' : 'default'}
            sub={fleetReady ? 'Each one is listed below' : 'Record checks only, until the nightly data is in'}
          />
          <Kpi
            label="Nightly data"
            value={fleet.loading ? waiting : fleet.error ? 'Did not load' : formatNzDateTime(fleet.indexGeneratedAt)}
            tone={fleet.error ? 'bad' : 'default'}
            sub="Use and stack status are read from it"
          />
        </KpiGrid>
      </Section>

      <Section title="Trials" note="Ended trials and trials with no end date first">
        <Panel title="Clients on a trial" subtitle="Active clients only. Use is for the last 30 days." flush>
          {!ready && <Empty>{waiting}</Empty>}
          {ready && trials.length === 0 && <Empty>No active client is on a trial.</Empty>}
          {ready && trials.length > 0 && (
            <div className="client-checks__scroll">
              <Table hover className="mb-0 client-checks__table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Started</th>
                    <th>Ends</th>
                    <th>Where it stands</th>
                    <th className="text-end" title="Conversations started in the last 30 days">
                      Conversations
                    </th>
                    <th className="text-end" title="The most people who typed a message on one day in the last 30 days">
                      Most people in a day
                    </th>
                    <th className="text-end" title="Credits used in the last 30 days">
                      Credits
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {trials.map((trial) => {
                    const use = fleetReady ? stackUseOf(trial.clientName) : null;
                    return (
                      <tr key={trial.clientName}>
                        {clientCell(trial.clientName)}
                        <td className="text-nowrap">{trial.startDate ? formatDay(trial.startDate) : ''}</td>
                        <td className="text-nowrap">{trial.endDate ? formatDay(trial.endDate) : ''}</td>
                        <td
                          className={`text-nowrap client-checks__timing client-checks__timing--${trial.timing.state}`}
                        >
                          {trial.timing.text}
                        </td>
                        <td className="text-end numeric">{use ? count(use.convs30) : ''}</td>
                        <td className="text-end numeric">{use ? count(use.peoplePeak30) : ''}</td>
                        <td className="text-end numeric">{use ? count(use.credits30) : ''}</td>
                        <td className="client-checks__actions">
                          <Link to={changeConfigLink(trial.clientName)} className="btn btn-sm btn-outline-secondary">
                            Change the config
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Panel>
      </Section>

      <Section title="Data checks" note="A check that finds nothing is not shown">
        {fleet.loading && (
          <div className="client-checks__wait">
            <Spinner animation="border" size="sm" className="me-2" />
            Reading the nightly data. The checks of use and stack status show when it is in.
          </div>
        )}
        {fleet.error && (
          <Alert variant="warning">
            {fleet.error} The checks of use and stack status cannot run.{' '}
            <Button size="sm" variant="outline-secondary" onClick={() => fleet.reload()}>
              Try again
            </Button>
          </Alert>
        )}
        {!ready && !clientsError && <Empty>{waiting}</Empty>}
        {ready && shownChecks.length === 0 && (
          <Empty>
            <CheckCircle className="client-checks__ok" /> No problems found.
          </Empty>
        )}
        {shownChecks.map(({ def, rows }) => (
          <Panel key={def.id} title={`${def.title} (${rows.length.toLocaleString()})`} subtitle={def.help} flush>
            <div className="client-checks__scroll">
              <Table hover className="mb-0 client-checks__table">
                <tbody>
                  {rows.map((finding) => (
                    <tr key={`${finding.check}-${finding.clientName}`}>
                      {clientCell(finding.clientName)}
                      <td className="client-checks__problem" title={finding.error}>
                        {finding.problem}
                        {finding.detail && <div className="client-checks__detail">{finding.detail}</div>}
                      </td>
                      <td className="client-checks__actions">{actionsFor(def, finding)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Panel>
        ))}
      </Section>

      <MarkInactiveDialog
        finding={marking}
        onClose={() => setMarking(null)}
        onMarked={(name) => {
          setNotice(`${name} is marked inactive.`);
          void flags.reload();
        }}
      />
    </div>
  );
}
